import type { HTMLAttributes, ReactNode } from 'react'

import { cx } from './core/classes.js'

/** Compact inline label for status, category, and metadata chips. */
export function Tag(props: Tag.Props) {
  const { children, className, dot = false, icon, intent = 'neutral', ...rest } = props

  return (
    <span
      className={cx(
        'inline-flex min-w-0 items-center gap-[4px] whitespace-nowrap rounded-button px-[6px] py-[2px] label-12',
        Tag.intentClassName[intent],
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span className="flex size-[12px] shrink-0 items-center justify-center [&>svg]:size-[12px]">
          {icon}
        </span>
      ) : dot ? (
        <span aria-hidden="true" className="size-[6px] shrink-0 rounded-full bg-current" />
      ) : null}
      <span className="min-w-0 overflow-hidden text-ellipsis">{children}</span>
    </span>
  )
}

export namespace Tag {
  /** Classes for each tag color intent. */
  export const intentClassName = {
    /** Accent metadata. */
    accent: 'bg-accent-subtle text-on-accent-subtle',
    /** Informational metadata. */
    info: 'bg-info-subtle text-info',
    /** Negative metadata. */
    negative: 'bg-negative-subtle text-negative',
    /** Neutral metadata. */
    neutral: 'bg-secondary text-foreground-secondary',
    /** Positive metadata. */
    positive: 'bg-positive-subtle text-positive',
    /** Warning metadata. */
    warning: 'bg-warning-subtle text-warning',
  } as const

  /** Tag color intent. */
  export type Intent = keyof typeof intentClassName

  /** Props for {@link Tag}. */
  export type Props = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
    /** Tag label. */
    children: ReactNode
    /** Show a small leading dot when no icon is provided. @default false */
    dot?: boolean | undefined
    /** Optional leading icon. */
    icon?: ReactNode | undefined
    /** Tag color intent. @default 'neutral' */
    intent?: Intent | undefined
  }
}
